/**
 * Adaptador de Richpanel. Este fichero es el ÚNICO sitio de la app que sabe qué
 * proveedor hay detrás del chat de soporte: su API, su selector en el DOM y la
 * forma de su script. Cambiar de proveedor es escribir otro fichero con estas
 * mismas exportaciones y tocar el import de `./index.js`.
 *
 * ## Lo que el snippet oficial dice y la documentación no
 *
 * El cargador que da el panel (Live chat → Getting started) se aparta de lo que
 * publica `developer.richpanel.com/docs/getting-started` en tres cosas, y las
 * tres importan:
 *
 * 1. **El host es otro.** Los docs dicen `api.richpanel.com/v2/j/<clave>`; el
 *    snippet real carga `cdn.richpanel.com/js/richpanel-root.js`. Es el que hay
 *    que permitir el dia que el nginx del frontend sirva un CSP.
 * 2. **`load()` ignora su argumento.** En el snippet, el `appClientId` va dentro
 *    del `src` y el parametro de la funcion no se usa en ningun sitio. Asi que
 *    aqui se inyecta la etiqueta directamente en vez de reproducir un
 *    `load(clave)` que solo aparenta estar parametrizado.
 * 3. **`identify` NO viene en la cola.** El cargador solo deja preparados
 *    `track`, `debug` y `atr`. Con Crisp se podia empujar la identidad antes de
 *    que el script cargara porque su cola la procesaba al inicializarse; aqui
 *    eso reventaria. Ver `identify` mas abajo.
 */

// Identificador publico de la app de Richpanel. Viaja en el HTML de cualquier
// pagina que lleve el widget, asi que no es un secreto y va en el repo, igual
// que estaba el de Crisp. El API SECRET es otra cosa y NO pinta nada aqui: sirve
// para firmar la identidad verificada y se usa desde el servidor.
//
// Hay UNA APP POR ENTORNO, cada una con su instalacion (dominio verificado), su
// brand y su secret de Custom Connector: el `RICHPANEL_API_SECRET` del servidor
// tiene que ser el de la app que carga ese mismo entorno, o la identidad no se
// descifra y el messenger vuelve a pedir nombre y correo.
const APP_CLIENT_ID_PROD = 'tapstar4444';
const APP_CLIENT_ID_STAGING = 'tapstar4443';

/**
 * La app de Richpanel que toca a un `VITE_APP_ENV`.
 *
 * Produccion solo con `'prod'` exacto (el `APP_ENV` real, el mismo que miran
 * `main.jsx` y `PosthogRecordingGate.jsx`); todo lo demas —staging, local, un
 * build sin la variable— va a staging. Al reves seria peor: un build de pruebas
 * mandaria conversaciones a la bandeja de clientes reales.
 */
export function appClientIdDe(env) {
  return env === 'prod' ? APP_CLIENT_ID_PROD : APP_CLIENT_ID_STAGING;
}

const RICHPANEL_APP_CLIENT_ID = appClientIdDe(import.meta.env.VITE_APP_ENV);

const SCRIPT_SRC = `https://cdn.richpanel.com/js/richpanel-root.js?appClientId=${RICHPANEL_APP_CLIENT_ID}`;

// Los unicos metodos que el cargador deja en cola antes de que el script cargue.
const QUEUED_METHODS = ['track', 'debug', 'atr'];

export const PROVIDER_NAME = 'richpanel';

/**
 * ## Que cuelga Richpanel del `<body>`
 *
 * No una caja, sino DOS, y en fases sucesivas: la primera desaparece cuando
 * aparece la segunda, asi que quien lo mire una sola vez solo vera la mitad del
 * contrato. Medido sobre produccion el 2026-09-03.
 *
 * **Fase 1, el cargador.** Desde que carga el script hasta el primer click:
 * `div.richpanel-micro`, `position: static` y de altura cero, con dos hijos
 * `fixed` a `z-index: 2147483647` — `.rp-micro-app-dummy-icon-container` (el
 * lanzador, 100x100) y `.rp-micro-app-messenger` (el esqueleto del panel,
 * 375x815, con la clase `rp-micro-app-hide` mientras esta cerrado).
 *
 * **Fase 2, el messenger de verdad.** Al abrirlo por primera vez, Richpanel
 * BORRA el `div.richpanel-micro` entero y cuelga en su lugar
 * `iframe#richpanel_messenger_iframe`, ya `position: fixed` y con el mismo
 * z-index. Su clase codifica el estado y el tamaño va con ella:
 * `richpanel-messenger-closed` mide 120x120 —la burbuja pasa a pintarse DENTRO
 * del iframe— y `richpanel-messenger-opened`, 450x911 en escritorio.
 *
 * La fase 2 es la que se paso por alto las dos veces que se escribio esto: no
 * existe hasta que alguien hace click, asi que no sale en ninguna inspeccion del
 * DOM recien cargado. De ahi salio el fallo que dejo el chat inutilizable en
 * produccion — ver `ensureGlobalStyles` en `./index.js`.
 */

/**
 * La caja `static`. Para desplazarla en movil hay que convertirla en una caja
 * fija a pantalla completa y transformar esa: el `position: fixed` esta en sus
 * hijos, y transformar un ancestro no fijo se lo rompe (pasarian a scrollear con
 * la pagina). Es el mismo apaño que hacia falta con Crisp.
 */
export const STATIC_ROOT_SELECTOR = '.richpanel-micro';

/**
 * La caja que ya es `position: fixed`, que basta con transformar.
 *
 * Va aparte y no en la misma regla que la anterior porque es un `<iframe>`: en
 * NUESTRO documento no tiene ni un hijo, asi que cualquier regla que se apoye en
 * `> *` para deshacer algo —los `pointer-events`, sin ir mas lejos— no tendria
 * donde agarrarse y dejaria un panel que no se puede tocar.
 */
export const FIXED_ROOT_SELECTOR = '#richpanel_messenger_iframe';

/** Las dos, que es lo que hay que esconder fuera de las paginas con chat. */
export const WIDGET_ROOT_SELECTOR = `${STATIC_ROOT_SELECTOR}, ${FIXED_ROOT_SELECTOR}`;

/**
 * Solo la BURBUJA, en sus dos fases, y nunca el panel. Es lo que se esconde
 * donde el chat se abre desde un boton propio (el menu del avatar en la app):
 *
 * - Fase 1: el lanzador `.rp-micro-app-dummy-icon-container`. Su hermano
 *   `.rp-micro-app-messenger` es el panel y tiene que quedar visible.
 * - Fase 2: el iframe con la clase de CERRADO, que mide 120x120 y es la burbuja
 *   pintada dentro. Abierto cambia a `richpanel-messenger-opened` y deja de
 *   casar, asi que el panel se ve.
 */
export const LAUNCHER_SELECTOR = '.rp-micro-app-dummy-icon-container, '
  + `${FIXED_ROOT_SELECTOR}.richpanel-messenger-closed`;

/**
 * ## Ajustes nativos de posicion que Richpanel ya trae
 *
 * El panel de administracion sirve un `control` con `messengerIconPosition`
 * ("right"), `bottomSpacing` (20) y `sideSpacing` (20), y el objeto del widget
 * expone `setMessengerConfiguration`. Merece la pena comprobar si cambiar
 * `bottomSpacing` en caliente mueve la burbuja: seria una forma mas limpia de
 * subirla sobre la barra inferior que sobreescribir su CSS.
 *
 * Ojo: ese ajuste es GLOBAL. Nuestro problema no lo es —la burbuja solo debe
 * subir en movil y dentro de la app con sesion iniciada, no en la landing— asi
 * que como mucho serviria para fijar la posicion base.
 */

/**
 * El objeto del widget. NO es `window.richpanel` (ese es el de seguimiento:
 * `track`, `atr`, `identify`); el widget vive en `window.Richpanel`, con
 * mayuscula, y es el mismo objeto que `window.RichpanelAppProxy`.
 *
 * Nada de esto esta documentado. Sale de inspeccionar el objeto ya cargado en el
 * navegador el 2026-08-26, asi que es un contrato que Richpanel puede cambiar sin
 * avisar: todo lo que lo usa comprueba antes que la funcion exista.
 */
function widget() {
  return /** @type {any} */ (window).Richpanel;
}

/**
 * Inyecta el script. Idempotente.
 *
 * Reproduce la cola del snippet oficial en vez de pegarlo tal cual: el original
 * declara `mth`, `sk`, `a` e `i` sin `var`, lo que en un modulo ES —que corre en
 * modo estricto— es un ReferenceError.
 */
export function injectScript() {
  const w = /** @type {any} */ (window);
  if (w.richpanel) return;
  const queue = [];
  queue.q = [];
  for (const name of QUEUED_METHODS) {
    queue[name] = (...args) => { queue.q.push([name, ...args]); };
  }
  // `load` existe solo para que nadie que copie el snippet oficial se encuentre
  // un hueco: en el original tampoco hace nada util, porque el appClientId ya va
  // dentro del `src` y el argumento se ignora.
  queue.load = () => {};
  queue.ensure_rpuid = '';
  w.richpanel = queue;
  const s = document.createElement('script');
  s.src = SCRIPT_SRC;
  s.async = true;
  document.head.appendChild(s);
}

/**
 * La identidad VERIFICADA: el payload que cifra el servidor con el API Secret
 * (`/api/v1/auth/support-identity/`). Sin ella el messenger trata al usuario
 * como visitante y le pide nombre y correo aunque `identify` se los haya dado.
 *
 * Richpanel la lee al cargar su script, asi que solo sirve si se llama ANTES de
 * `injectScript()`. Despues no hace nada hasta la siguiente carga de pagina.
 */
export function setVerifiedIdentity(settings) {
  if (!settings) return;
  /** @type {any} */ (window).richpanelSettings = settings;
}

/** La UI del lanzador. Existe desde que carga el script, ya en la fase 1. */
function ui() {
  return widget()?.microAppUI;
}

export function showWidget() {
  ui()?.show?.();
}

export function hideWidget() {
  ui()?.hide?.();
}

/**
 * El hash con el que Richpanel abre su propio panel. Lo pone su lanzador al
 * hacer click —de ahi el `#rp-customer-widget-home` que aparece en la barra de
 * direcciones— y lo quita al cerrar; el widget se entera por `hashchange`
 * (`microAppUI.hashChangeEventHandler`).
 */
const OPEN_HASH = '#rp-customer-widget-home';

/**
 * Abre el panel. Devuelve `false` si no lo consiguio, para que quien llama pueda
 * ofrecer el correo de ventas en vez de dejar un boton muerto.
 *
 * Va por el hash y no por la API del objeto porque la API NO abre nada, medido
 * en produccion el 2026-09-03: `showMessenger` y `showTheMessengerFrame` no
 * hacen nada, y `toggleMessenger` solo voltea la bandera `isMessengerOpen`, que
 * ademas se queda desincronizada —vale `true` con el panel cerrado— porque
 * quien cierra es el aspa de DENTRO del iframe y el documento de fuera no se
 * entera. Por eso tampoco se puede consultar para saber si ya estaba abierto: la
 * comprobacion que habia aqui daba `true` siempre y dejaba muertos los botones
 * de "hablar con soporte" de la app.
 *
 * El click sintetico que `openSupportChat` intentaria despues tampoco valdria de
 * respaldo: en fase 2 la burbuja se pinta dentro de un <iframe> de otro origen y
 * un evento sintetico no entra ahi.
 */
export function openWidget() {
  // Sin `microAppUI` el script no ha cargado (bloqueador de anuncios, red
  // caida): poner el hash no abriria nada y encima dejaria basura en la barra de
  // direcciones. Mejor decir que no se pudo y que quien llama ofrezca el correo.
  if (!ui()) return false;
  if (window.location.hash === OPEN_HASH) return true;
  window.location.hash = OPEN_HASH;
  return true;
}

/**
 * Identifica al usuario y adjunta los datos de su cuenta.
 *
 * `identify` no esta en la cola del arranque —el cargador solo deja preparados
 * `track`, `debug` y `atr`—, asi que llamarlo antes de que el script cargue seria
 * un TypeError. Los atributos van por `atr`, que SI esta en cola y por tanto es
 * seguro en cualquier momento; la identidad espera a que el script este listo.
 *
 * El sondeo es feo, pero el cargador no ofrece ningun callback ni evento de "ya
 * estoy". Se rinde al minuto para no dejar un temporizador vivo indefinidamente
 * si el script no llega (bloqueador de anuncios, red caida).
 */
export function identify(identity, traits) {
  const q = /** @type {any} */ (window).richpanel;
  if (!q) return;

  // Los campos estandar de `identify` segun la documentacion: `uid`, `email`,
  // `phone`, `firstName`, `lastName`. NO hay `name`: el que se mandaba antes no
  // lo leia nadie. Los traits (plan, asientos...) van detras como atributos.
  const payload = { ...traits, uid: identity.id };
  for (const key of ['email', 'firstName', 'lastName', 'phone']) {
    if (identity[key]) payload[key] = identity[key];
  }
  q.atr?.(payload);

  const send = () => {
    const rp = /** @type {any} */ (window).richpanel;
    if (typeof rp?.identify !== 'function') return false;
    rp.identify(identity.id, payload);
    return true;
  };
  if (send()) return;

  let waited = 0;
  const STEP_MS = 250;
  const GIVE_UP_MS = 60000;
  const timer = setInterval(() => {
    waited += STEP_MS;
    if (send() || waited >= GIVE_UP_MS) clearInterval(timer);
  }, STEP_MS);
}
